/**
 * <axiom-header> — sticky / hide-on-scroll behaviour, transparent state,
 * dropdown panels with hover intent, and the mobile menu overlay.
 */
(function () {
  'use strict';

  if (customElements.get('axiom-header')) return;

  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var desktop = window.matchMedia('(min-width: 990px)');

  class AxiomHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section') || this;
      this.root = document.documentElement;
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.sticky = this.dataset.sticky;
      this.wrapper.dataset.sticky = this.sticky;
      this.transparent = this.hasAttribute('data-transparent');
      this.wrapper.toggleAttribute('data-transparent', this.transparent);
      this.lastY = window.scrollY;

      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this);
      this.measure();

      window.addEventListener('scroll', () => this.requestScroll(), { passive: true, signal });
      this.onScroll();

      this.initDropdowns(signal);
      this.initMobileMenu(signal);
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.root.classList.remove('header-hidden');
      this.closeMobile(false);
    }

    measure() {
      const bar = this.querySelector('.header__inner');
      this.root.style.setProperty('--header-height', (bar ? bar.offsetHeight : this.offsetHeight) + 'px');
    }

    requestScroll() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.onScroll();
      });
    }

    onScroll() {
      const y = Math.max(window.scrollY, 0);
      const scrolled = y > 8;
      this.classList.toggle('is-scrolled', scrolled);
      this.wrapper.classList.toggle('is-scrolled', scrolled);

      if (this.transparent) {
        const solid = scrolled || this.hasOpenPanel();
        const scheme = solid ? this.dataset.scheme : this.dataset.transparentScheme;
        this.classList.remove('color-' + this.dataset.scheme, 'color-' + this.dataset.transparentScheme);
        this.classList.add('color-' + scheme);
        this.classList.toggle('is-transparent', !solid);
      }

      if (this.sticky === 'scroll-up' && !this.hasOpenPanel()) {
        const threshold = this.offsetHeight * 2;
        const down = y > this.lastY + 4;
        const up = y < this.lastY - 4;
        if (down && y > threshold) this.root.classList.add('header-hidden');
        else if (up || y <= threshold) this.root.classList.remove('header-hidden');
      }
      this.lastY = y;
    }

    hasOpenPanel() {
      return Boolean(this.querySelector('[data-dropdown-trigger][aria-expanded="true"]')) || this.mobileOpen;
    }

    /* ---------------- Dropdowns ---------------- */
    initDropdowns(signal) {
      this.triggers = Array.from(this.querySelectorAll('[data-dropdown-trigger]'));
      this.triggers.forEach((trigger) => {
        const item = trigger.closest('.header__item');
        trigger.addEventListener('click', () => {
          const open = trigger.getAttribute('aria-expanded') === 'true';
          this.setDropdown(trigger, !open);
        }, { signal });

        item.addEventListener('pointerenter', () => {
          if (!finePointer.matches) return;
          clearTimeout(this.closeTimer);
          this.openTimer = setTimeout(() => this.setDropdown(trigger, true), 70);
        }, { signal });

        item.addEventListener('pointerleave', () => {
          if (!finePointer.matches) return;
          clearTimeout(this.openTimer);
          this.closeTimer = setTimeout(() => this.setDropdown(trigger, false), 160);
        }, { signal });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.triggers.find((t) => t.getAttribute('aria-expanded') === 'true');
        if (open) {
          this.setDropdown(open, false);
          open.focus();
        }
        if (this.mobileOpen) this.closeMobile(true);
      }, { signal });

      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.closeAllDropdowns();
      }, { signal });

      this.addEventListener('focusout', (event) => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeAllDropdowns();
      }, { signal });
    }

    setDropdown(trigger, open) {
      if (open) {
        this.triggers.forEach((other) => {
          if (other !== trigger) this.setDropdown(other, false);
        });
      }
      trigger.setAttribute('aria-expanded', String(open));
      trigger.closest('.header__item').classList.toggle('is-open', open);
      this.classList.toggle('has-dropdown-open', this.triggers.some((t) => t.getAttribute('aria-expanded') === 'true'));
      this.onScroll();
    }

    closeAllDropdowns() {
      this.triggers.forEach((trigger) => {
        if (trigger.getAttribute('aria-expanded') === 'true') this.setDropdown(trigger, false);
      });
    }

    /* ---------------- Mobile menu ---------------- */
    initMobileMenu(signal) {
      this.toggle = this.querySelector('[data-menu-toggle]');
      this.menu = this.querySelector('[data-mobile-menu]');
      if (!this.toggle || !this.menu) return;

      this.toggle.addEventListener('click', () => {
        if (this.mobileOpen) this.closeMobile(true);
        else this.openMobile();
      }, { signal });

      this.menu.addEventListener('keydown', (event) => window.Axiom?.trapFocus(this, event), { signal });
      this.menu.addEventListener('click', (event) => {
        if (event.target.closest('a[href]')) this.closeMobile(false);
      }, { signal });

      desktop.addEventListener('change', () => {
        if (desktop.matches && this.mobileOpen) this.closeMobile(false);
      }, { signal });
    }

    openMobile() {
      this.mobileOpen = true;
      this.root.classList.remove('header-hidden');
      // Start the overlay below the header bar, wherever it currently sits
      // (the announcement bar may be above it).
      this.menu.style.setProperty('--menu-top', Math.max(this.getBoundingClientRect().bottom, 0) + 'px');
      this.menu.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      this.classList.add('is-menu-open');
      window.Axiom?.lockScroll();
      requestAnimationFrame(() => this.menu.classList.add('is-open'));
      this.onScroll();
    }

    closeMobile(returnFocus) {
      if (!this.mobileOpen || !this.menu) return;
      this.mobileOpen = false;
      this.toggle.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-menu-open');
      this.menu.classList.remove('is-open');
      window.Axiom?.unlockScroll();
      const hide = () => {
        if (!this.mobileOpen) this.menu.hidden = true;
      };
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) hide();
      else setTimeout(hide, 450);
      if (returnFocus) this.toggle.focus();
      this.onScroll();
    }
  }

  customElements.define('axiom-header', AxiomHeader);
})();
